import { useMemo, useState } from "react";
import AdminListShell, { AdminListRow } from "@/components/admin/AdminListShell";
import AdminDetailDialog from "@/components/admin/AdminDetailDialog";
import ModalDossierEmbed from "@/components/shared/ModalDossierEmbed";
import TransactionTrackingCard from "@/components/shared/TransactionTrackingCard";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { api } from "@/lib/api";
import { SELLER_PAYOUT_HOURS } from "@/lib/businessDays";
import LiveCountdown from "@/components/shared/LiveCountdown";
import { litigeRowStatusLabelFr } from "@/lib/marketplaceStatusLabels";
import { resolveTransactionTracking } from "@/lib/transactionTracking";
import { toast } from "sonner";
import { Headphones, Loader2, Scale, Trash2 } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import CallAiAnalysisBlock from "@/components/admin/CallAiAnalysisBlock";
import AdminMoneyTrail from "@/components/admin/AdminMoneyTrail";
import AdminDossierCallHistory from "@/components/admin/AdminDossierCallHistory";

function formatDt(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleString("fr-FR");
}

function adminTxStatutLabelFr(st) {
  const s = String(st || "").trim().toLowerCase();
  const map = {
    en_attente: "En attente paiement",
    paye: "Séquestre (payé)",
    appel_en_cours: "Appel en cours",
    appel_effectue: "Décision requise",
    fonds_debloques: "RDV confirmé",
    litige: "Litige",
    rembourse: "Remboursé",
    annule: "Annulé",
    echec_paiement: "Échec paiement",
  };
  return map[s] || (s ? String(st) : "—");
}

function fluxListLine(tx) {
  const tr = tx?.tracking || resolveTransactionTracking(tx, { litiges: tx?.litiges || [], role: "admin" });
  if (tx.stripe_connect_transfer_id) {
    return tr.money_location_label || "Versé au vendeur";
  }
  if (tr.list_hint) return tr.list_hint;
  return payoutSummaryFr(tx);
}

function payoutSummaryFr(tx) {
  if (tx.stripe_connect_transfer_id) {
    return tx.seller_payout_transferred_at
      ? `Versé le ${formatDt(tx.seller_payout_transferred_at)}`
      : "Versé (Connect)";
  }
  if (tx.seller_payout_failed_at) {
    return `Échec versement · ${tx.seller_payout_failure_reason || "voir détail"}`;
  }
  if (tx.seller_payout_on_hold) return "Versement suspendu (litige)";
  if (tx.stripe_charge_dispute_id) return `Chargeback Stripe · ${tx.stripe_charge_dispute_status || "—"}`;
  if (tx.payout_due) return `Versement dû (délai ${SELLER_PAYOUT_HOURS} h écoulé)`;
  if (tx.seller_payout_available_at) {
    return `Versement prévu après ${formatDt(tx.seller_payout_available_at)}`;
  }
  if (String(tx.statut) === "fonds_debloques") return "En attente de versement (cron)";
  return "—";
}

export default function AdminTransactionsTab({
  transactions = [],
  query = "",
  onQueryChange,
  filter = "all",
  onFilterChange,
  detailId = null,
  onDetailIdChange,
  /** Empêche la fermeture de la modale transaction tant qu’une modale empilée est ouverte. */
  detailCloseLocked = false,
  /** Masque la modale transaction (id conservé) quand une modale empilée est affichée. */
  detailDialogHidden = false,
  onOpenLitige,
  onOpenUser,
  onRefresh,
  onListenRecording,
  recordingLoadTid = null,
}) {
  const [txPane, setTxPane] = useState("main");
  const [releasingId, setReleasingId] = useState(null);
  const [voidConfirmOpen, setVoidConfirmOpen] = useState(false);
  const [voidingId, setVoidingId] = useState(null);

  const filtered = useMemo(() => {
    const q = String(query || "").trim().toLowerCase();
    let list = Array.isArray(transactions) ? [...transactions] : [];
    if (filter === "attention") {
      list = list.filter((t) => t.needs_attention);
    }
    if (q) {
      list = list.filter((t) => {
        const hay = [
          t.transaction_id,
          t.id,
          t.statut,
          t.dossier?.title,
          t.buyer?.name,
          t.buyer?.email,
          t.seller?.name,
          t.seller?.email,
          t.stripe_payment_intent_id,
          t.stripe_connect_transfer_id,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        return hay.includes(q);
      });
    }
    return list;
  }, [transactions, query, filter]);

  const detailRow = useMemo(() => {
    if (!detailId) return null;
    return filtered.find((t) => String(t.transaction_id || t.id) === String(detailId)) ||
      transactions.find((t) => String(t.transaction_id || t.id) === String(detailId)) ||
      null;
  }, [detailId, filtered, transactions]);

  const attentionCount = useMemo(
    () => (Array.isArray(transactions) ? transactions : []).filter((t) => t.needs_attention).length,
    [transactions],
  );

  const handleReleasePayout = async (tx) => {
    const id = String(tx?.transaction_id || tx?.id || "");
    if (!id) return;
    setReleasingId(id);
    try {
      await api.post("/admin/releasePayout", { transaction_id: id });
      toast.success("Versement vendeur déclenché.");
      await onRefresh?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Échec du versement");
    } finally {
      setReleasingId(null);
    }
  };

  const canVoidTransaction = (tx) => {
    if (!tx) return false;
    if (tx.stripe_connect_transfer_id || tx.seller_payout_transferred_at) return false;
    const st = String(tx.statut || "").toLowerCase();
    return ["en_attente", "paye", "appel_en_cours", "appel_effectue", "litige"].includes(st);
  };

  const handleVoidTransaction = async () => {
    const tx = detailRow;
    const id = String(tx?.transaction_id || tx?.id || "");
    if (!id) return;
    setVoidingId(id);
    try {
      await api.post("/admin/transactionVoid", { transaction_id: id, confirm: "VOID" });
      toast.success("Transaction annulée — remboursement acheteur si applicable.");
      setVoidConfirmOpen(false);
      onDetailIdChange?.(null);
      await onRefresh?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Annulation impossible");
    } finally {
      setVoidingId(null);
    }
  };

  return (
    <>
      <p className="mb-3 text-sm text-slate-600">
        Toutes les transactions marketplace : séquestre, capture, délai de versement vendeur ({SELLER_PAYOUT_HOURS} h),
        litiges et alertes Stripe. Cliquez une ligne pour le détail.
      </p>
      <div className="mb-3 flex flex-wrap gap-2">
        <Button
          type="button"
          size="sm"
          variant={filter === "all" ? "default" : "outline"}
          onClick={() => onFilterChange?.("all")}
          data-testid="admin-tx-filter-all"
        >
          Toutes
        </Button>
        <Button
          type="button"
          size="sm"
          variant={filter === "attention" ? "default" : "outline"}
          onClick={() => onFilterChange?.("attention")}
          data-testid="admin-tx-filter-attention"
        >
          À traiter {attentionCount > 0 ? `(${attentionCount})` : ""}
        </Button>
      </div>

      <div
        className="mb-4 rounded-xl border border-sky-200 bg-sky-50/80 px-4 py-3 text-sm text-sky-950"
        data-testid="admin-tx-stripe-hint"
      >
        <strong>Suivre l&apos;argent :</strong> cliquez une vente ci-dessous → bloc{" "}
        <strong>« Où est l&apos;argent ? »</strong>. Dans Stripe, évitez seul l&apos;onglet <em>Solde</em> (pas
        d&apos;acheteur/vendeur). Liens Stripe depuis le détail de chaque transaction.
      </div>

      <AdminListShell
        searchId="admin-transactions-search"
        searchPlaceholder="Annonce, acheteur, vendeur, ID transaction, Stripe…"
        searchValue={query}
        onSearchChange={(e) => onQueryChange?.(e.target.value)}
        count={filtered.length}
        total={transactions.length}
        countNoun="transaction"
        emptyMessage={
          transactions.length === 0
            ? "Aucune transaction."
            : filter === "attention"
              ? "Aucune transaction ne nécessite d’attention pour ce filtre."
              : "Aucune transaction ne correspond à la recherche."
        }
        listTestId="admin-transactions-list"
        columnGridClassName="sm:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto_auto_1.25rem]"
        columns={[
          { key: "annonce", label: "Annonce / acheteur", className: "sm:col-start-1" },
          { key: "argent", label: "Argent / délai", className: "sm:col-start-2" },
          { key: "flux", label: "Versement", className: "sm:col-start-3" },
          { key: "etat", label: "État", className: "sm:col-start-4" },
          { key: "go", label: "", className: "sm:col-start-5" },
        ]}
      >
        {filtered.map((t) => {
          const rowKey = String(t.transaction_id || t.id);
          const title = t.dossier?.title || `Transaction ${rowKey.slice(0, 8)}…`;
          return (
            <AdminListRow
              key={rowKey}
              testId={`admin-tx-row-${rowKey}`}
              gridClassName="sm:grid sm:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto_auto_1.25rem] sm:items-center sm:gap-2"
              onClick={() => onDetailIdChange?.(rowKey)}
              className={
                detailId === rowKey ? "bg-sky-50/80 ring-1 ring-inset ring-sky-300" : undefined
              }
            >
              <div className="min-w-0 sm:col-span-1">
                <div className="truncate text-sm font-semibold text-slate-900">{title}</div>
                <div className="truncate text-[11px] text-slate-500">
                  {t.buyer?.name || "—"}
                  {t.buyer?.email ? ` · ${t.buyer.email}` : ""}
                </div>
              </div>
              <div className="hidden min-w-0 sm:block sm:col-span-1">
                <div className="truncate text-xs text-slate-700">
                  {t.tracking?.money_location_label ||
                    resolveTransactionTracking(t, { litiges: t.litiges, role: "admin" }).money_location_label}
                </div>
                {t.seller_payout_available_at &&
                !t.stripe_connect_transfer_id &&
                t.tracking?.hours_until_payout != null &&
                t.tracking.hours_until_payout > 0 ? (
                  <div className="truncate text-[10px] text-slate-500">
                    Délai {SELLER_PAYOUT_HOURS} h ·{" "}
                    <LiveCountdown
                      targetIso={t.seller_payout_available_at}
                      prefix=""
                      doneLabel="échu"
                      tabular
                    />
                  </div>
                ) : null}
              </div>
              <div className="hidden min-w-0 sm:block sm:col-span-1">
                <div className="truncate text-xs text-slate-600">{fluxListLine(t)}</div>
              </div>
              <Badge
                className={cn(
                  "shrink-0",
                  t.needs_attention ? "bg-amber-100 text-amber-900" : "bg-slate-100 text-slate-700",
                )}
              >
                {adminTxStatutLabelFr(t.statut)}
              </Badge>
            </AdminListRow>
          );
        })}
      </AdminListShell>

      <AdminDetailDialog
        open={Boolean(detailId) && !detailDialogHidden}
        onOpenChange={(open) => {
          if (!open && detailCloseLocked) return;
          if (!open) {
            onDetailIdChange?.(null);
            setTxPane("main");
          }
        }}
        onBack={txPane === "dossier" ? () => setTxPane("main") : undefined}
        size="xl"
        testId="admin-transaction-detail-dialog"
        title={txPane === "dossier" ? "Fiche annonce" : detailRow?.dossier?.title || "Transaction"}
        description={txPane === "main" && detailRow ? formatDt(detailRow.created_at) : undefined}
      >
        {txPane === "dossier" && detailRow?.dossier_id ? (
          <ModalDossierEmbed dossierId={detailRow.dossier_id} onBack={() => setTxPane("main")} />
        ) : detailRow ? (
          <div className="space-y-4 text-sm">
            <div className="flex flex-wrap gap-2">
              <Badge>{adminTxStatutLabelFr(detailRow.statut)}</Badge>
              {detailRow.needs_attention ? (
                <Badge className="bg-amber-100 text-amber-900">À surveiller</Badge>
              ) : null}
            </div>

            <AdminMoneyTrail tx={detailRow} />

            <TransactionTrackingCard
              tx={detailRow}
              litiges={detailRow.litiges || []}
              role="admin"
            />

            <div className="grid gap-3 sm:grid-cols-2">
              <div className="rounded-lg border border-slate-200 bg-slate-50/80 p-3">
                <div className="text-[10px] font-semibold uppercase text-slate-500">Montants</div>
                <div className="mt-1 space-y-0.5 tabular-nums">
                  <div>
                    Total acheteur :{" "}
                    <strong>
                      {detailRow.buyer_total != null
                        ? `${Number(detailRow.buyer_total).toLocaleString("fr-FR")} €`
                        : "—"}
                    </strong>
                  </div>
                  <div>
                    Prix deal :{" "}
                    <strong>
                      {detailRow.price != null ? `${Number(detailRow.price).toLocaleString("fr-FR")} €` : "—"}
                    </strong>
                  </div>
                  <div>
                    Commission :{" "}
                    <strong>
                      {detailRow.commission != null
                        ? `${Number(detailRow.commission).toLocaleString("fr-FR")} €`
                        : "—"}
                    </strong>
                  </div>
                </div>
              </div>
              <div className="rounded-lg border border-slate-200 bg-slate-50/80 p-3">
                <div className="text-[10px] font-semibold uppercase text-slate-500">Versement vendeur</div>
                <p className="mt-1 text-slate-700">{payoutSummaryFr(detailRow)}</p>
                {detailRow.capture_confirmed_at ? (
                  <p className="mt-1 text-xs text-slate-500">RDV confirmé le {formatDt(detailRow.capture_confirmed_at)}</p>
                ) : null}
              </div>
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <div className="text-[10px] font-semibold uppercase text-slate-500">Acheteur</div>
                <p className="font-medium">{detailRow.buyer?.name || "—"}</p>
                <p className="text-xs text-slate-500">{detailRow.buyer?.email || ""}</p>
              </div>
              <div>
                <div className="text-[10px] font-semibold uppercase text-slate-500">Vendeur</div>
                <p className="font-medium">{detailRow.seller?.name || "—"}</p>
                <p className="text-xs text-slate-500">
                  {detailRow.seller?.email || ""}
                  {detailRow.seller?.stripe_onboarding_complete === false ? " · Connect incomplet" : ""}
                </p>
              </div>
            </div>

            {(detailRow.litiges || []).length > 0 ? (
              <div className="rounded-lg border border-slate-200 p-3">
                <div className="text-[10px] font-semibold uppercase text-slate-500 mb-2">Litiges RdvMarket</div>
                <ul className="space-y-2">
                  {detailRow.litiges.map((l) => {
                    const lid = l.id || l.dispute_id;
                    return (
                      <li key={lid} className="flex flex-wrap items-center justify-between gap-2 text-xs">
                        <span>
                          {litigeRowStatusLabelFr(l.status ?? l.statut)} — {l.reason || "—"}
                        </span>
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          onClick={() =>
                            onOpenLitige?.(lid, {
                              returnTo: "transaction",
                              transactionId: String(detailRow.transaction_id || detailRow.id || detailId || ""),
                            })
                          }
                        >
                          <Scale className="h-3.5 w-3.5 mr-1" />
                          Ouvrir
                        </Button>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ) : null}

            <div className="rounded-lg border border-slate-200 bg-slate-50/80 p-3 space-y-3">
              <div className="text-[10px] font-semibold uppercase text-slate-500">
                Appel téléphonique (cette transaction)
              </div>
              <p className="text-[11px] text-slate-500 -mt-1">
                Après chaque appel, l’acheteur indique RDV obtenu ou refus. L’analyse IA ne s’applique qu’aux refus de
                dossier. Historique complet du dossier ci-dessous.
              </p>
              {detailRow.call_summary ? (
                <ul className="text-xs text-slate-700 space-y-0.5">
                  <li>
                    Mise en relation :{" "}
                    <strong>{detailRow.call_summary.bridged ? "oui" : "non"}</strong>
                  </li>
                  <li>
                    Enregistrement en base :{" "}
                    <strong>
                      {detailRow.call_summary.recording_available ? "oui" : "non / en attente"}
                    </strong>
                  </li>
                  {detailRow.call_summary.ended_at ? (
                    <li>Fin d’appel : {formatDt(detailRow.call_summary.ended_at)}</li>
                  ) : null}
                </ul>
              ) : (
                <p className="text-xs text-slate-500">Aucune session d’appel enregistrée pour cette transaction.</p>
              )}
              {onListenRecording ? (
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  disabled={recordingLoadTid === String(detailRow.transaction_id || detailRow.id)}
                  onClick={() => onListenRecording(detailRow.transaction_id || detailRow.id)}
                  data-testid="admin-tx-listen-recording"
                >
                  <Headphones className="h-3.5 w-3.5 mr-1" />
                  {recordingLoadTid === String(detailRow.transaction_id || detailRow.id)
                    ? "Chargement…"
                    : "Écouter l’enregistrement"}
                </Button>
              ) : null}
              <div className="border-t border-slate-200 pt-3">
                <div className="text-[10px] font-semibold uppercase text-slate-500 mb-1.5">
                  Analyse IA (clôture appel)
                </div>
                <CallAiAnalysisBlock ia={detailRow.synthese_ia_transaction ?? detailRow.ai_analysis} />
              </div>
            </div>

            {detailRow.dossier_id ? (
              <AdminDossierCallHistory
                dossierId={detailRow.dossier_id}
                onListenRecording={onListenRecording}
                recordingLoadTid={recordingLoadTid}
              />
            ) : null}

            <div className="rounded-lg border border-dashed border-slate-200 p-3 text-xs text-slate-600 break-all">
              <div>PI : {detailRow.stripe_payment_intent_id || "—"}</div>
              <div>Transfer : {detailRow.stripe_connect_transfer_id || "—"}</div>
              {detailRow.stripe_charge_dispute_id ? (
                <div className="text-amber-800">
                  Chargeback : {detailRow.stripe_charge_dispute_id} ({detailRow.stripe_charge_dispute_status})
                </div>
              ) : null}
            </div>

            <div className="flex flex-wrap gap-2 border-t border-slate-100 pt-4">
              {canVoidTransaction(detailRow) ? (
                <Button
                  type="button"
                  size="sm"
                  variant="destructive"
                  disabled={voidingId === String(detailRow.transaction_id || detailRow.id)}
                  onClick={() => setVoidConfirmOpen(true)}
                  data-testid="admin-tx-void"
                >
                  {voidingId === String(detailRow.transaction_id || detailRow.id) ? (
                    <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                  ) : (
                    <Trash2 className="h-4 w-4 mr-1" />
                  )}
                  Annuler (exceptionnel)
                </Button>
              ) : null}
              {(detailRow.tracking?.can_admin_release_payout || detailRow.can_admin_release_payout) ? (
                <Button
                  type="button"
                  size="sm"
                  disabled={releasingId === String(detailRow.transaction_id || detailRow.id)}
                  onClick={() => handleReleasePayout(detailRow)}
                  data-testid="admin-tx-release-payout"
                >
                  {releasingId === String(detailRow.transaction_id || detailRow.id) ? (
                    <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                  ) : null}
                  Déclencher versement vendeur
                </Button>
              ) : null}
              {detailRow.dossier_id ? (
                <Button type="button" variant="outline" size="sm" onClick={() => setTxPane("dossier")}>
                  Fiche annonce
                </Button>
              ) : null}
              {detailRow.buyer?.user_id ? (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    onOpenUser?.(detailRow.buyer.user_id, {
                      returnTo: "transaction",
                      transactionId: String(detailRow.transaction_id || detailRow.id || detailId || ""),
                    })
                  }
                >
                  Acheteur
                </Button>
              ) : null}
              {detailRow.seller?.user_id ? (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    onOpenUser?.(detailRow.seller.user_id, {
                      returnTo: "transaction",
                      transactionId: String(detailRow.transaction_id || detailRow.id || detailId || ""),
                    })
                  }
                >
                  Vendeur
                </Button>
              ) : null}
            </div>
          </div>
        ) : null}
      </AdminDetailDialog>

      <AlertDialog open={voidConfirmOpen} onOpenChange={setVoidConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Annuler exceptionnellement cette transaction ?</AlertDialogTitle>
            <AlertDialogDescription>
              L&apos;acheteur sera remboursé sur Stripe si le paiement a été capturé. Le versement vendeur ne sera pas
              effectué. L&apos;annonce sera remise en ligne si aucune autre commande active ne la bloque. Action
              irréversible côté flux marketplace.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={Boolean(voidingId)}>Retour</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={Boolean(voidingId)}
              onClick={(e) => {
                e.preventDefault();
                void handleVoidTransaction();
              }}
            >
              {voidingId ? "Annulation…" : "Confirmer l’annulation"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
